import { useSelector } from 'react-redux'
import usePurchaseOrders from './usePurchaseOrders'
import { useState, useEffect } from 'react'

const useApprovals = () => {
  const purchaseOrdersHook = usePurchaseOrders()
  const { orders, updateOrderApproval, addOrderApproval } = purchaseOrdersHook

  // Get the current user from the store
  const currentUser = useSelector((state) => state.auth.agiliteUser)

  // Track orders that need the current user's approval
  const [pendingApprovals, setPendingApprovals] = useState([])

  // Track orders that the current user has already approved
  const [approvedByMe, setApprovedByMe] = useState([])

  // Filter the orders based on approval status
  useEffect(() => {
    if (!currentUser || !orders) return

    const pending = []
    const approved = []

    orders.forEach((order) => {
      // Check if this order has an approval step for the current user
      const myApprovalStep = order.approvalChain.find((step) => step.approverId === currentUser._id)

      if (myApprovalStep) {
        if (myApprovalStep.status === 'Pending') {
          pending.push(order)
        } else if (myApprovalStep.status === 'Approved') {
          approved.push(order)
        }
      }
    })

    setPendingApprovals(pending)
    setApprovedByMe(approved)
  }, [orders, currentUser])

  // Check if the current user can approve a specific order
  const canApprove = (order) => {
    if (!currentUser || !order) return false

    const myApprovalStep = order.approvalChain.find((step) => step.approverId === currentUser._id)

    return myApprovalStep && myApprovalStep.status === 'Pending'
  }

  // Check if the current user has already approved a specific order
  const hasApproved = (order) => {
    if (!currentUser || !order) return false

    const myApprovalStep = order.approvalChain.find((step) => step.approverId === currentUser._id)

    return myApprovalStep && myApprovalStep.status === 'Approved'
  }

  // Approve an order
  const approveOrder = (orderId, comments = '') => {
    if (!currentUser) return false

    return updateOrderApproval(orderId, currentUser._id, 'Approved')
  }

  // Reject an order
  const rejectOrder = (orderId, comments = '') => {
    if (!currentUser) return false

    return updateOrderApproval(orderId, currentUser._id, 'Rejected')
  }

  // Add a new approval step to an order
  const addApprovalStep = (orderId, approverInfo) => {
    const approval = {
      approverId: approverInfo.approverId,
      approverName: approverInfo.approverName,
      status: 'Pending',
      timestamp: null
    }

    return addOrderApproval(orderId, approval)
  }

  // Get all users who can approve orders (in a real app, this would come from an API)
  const getApprovers = () => {
    // Mock data - in a real app, this would be a more sophisticated function
    // that fetches users with approval permissions from the server
    return [
      {
        approverId: 'user-123',
        approverName: 'Dr. James Smith',
        department: 'Management',
        role: 'Department Head'
      },
      {
        approverId: 'user-456',
        approverName: 'Sarah Johnson',
        department: 'Finance',
        role: 'Financial Controller'
      },
      {
        approverId: 'user-789',
        approverName: 'Michael Brown',
        department: 'Procurement',
        role: 'Procurement Manager'
      }
    ]
  }

  // Get the approval status of an order
  const getOrderApprovalStatus = (order) => {
    if (!order || !order.approvalChain || order.approvalChain.length === 0) {
      return { status: 'No Approval Required', color: '#d9d9d9' }
    }

    const pendingApprovals = order.approvalChain.filter((step) => step.status === 'Pending').length
    const rejectedApprovals = order.approvalChain.filter((step) => step.status === 'Rejected').length
    const totalApprovals = order.approvalChain.length

    if (rejectedApprovals > 0) {
      return { status: 'Rejected', color: '#f5222d' }
    }

    if (pendingApprovals === 0) {
      return { status: 'Fully Approved', color: '#52c41a' }
    }

    const completedPercentage = Math.round(((totalApprovals - pendingApprovals) / totalApprovals) * 100)

    return {
      status: `Approval in Progress (${completedPercentage}%)`,
      color: '#1890ff',
      completedPercentage
    }
  }

  return {
    ...purchaseOrdersHook,
    pendingApprovals,
    approvedByMe,
    canApprove,
    hasApproved,
    approveOrder,
    rejectOrder,
    addApprovalStep,
    getApprovers,
    getOrderApprovalStatus
  }
}

export default useApprovals
